import * as icons from '@mui/icons-material';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { FC, useState } from 'react';
import { DatadogLinks } from './datadog-links';

/**
 * Class the bubble's hover rule targets to reveal the button — see
 * `inboundMenuBubbleSx` in message-types.
 */
export const DATADOG_MENU_BUTTON_CLASS = 'chat-menu-btn';

/**
 * Three-dot menu opening a message's Datadog links, mirroring the testchat
 * bubbles. Hidden until the bubble is hovered, so a long conversation isn't
 * covered in icons.
 *
 * Rendered only when there are links to show, so the bubble's reserved padding
 * and the button stay in sync — see the call site in ChatConversation.
 */
export const DatadogMessageMenu: FC<{ links: DatadogLinks }> = ({ links }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = !!anchorEl;

  const items: { label: string; href: string }[] = [
    { label: 'Logs', href: links.logsUrl },
    { label: 'LLM Traces', href: links.llmTracesUrl },
  ];

  return (
    <>
      <IconButton
        className={DATADOG_MENU_BUTTON_CLASS}
        size="small"
        aria-label="Datadog links for this message"
        aria-haspopup="true"
        aria-controls={isOpen ? 'datadog-message-menu' : undefined}
        aria-expanded={isOpen}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          position: 'absolute',
          top: '50%',
          right: 8,
          transform: 'translateY(-50%)',
          opacity: 0,
          padding: '2px',
          borderRadius: '50%',
          color: 'inherit',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
        }}
      >
        <icons.MoreVert fontSize="small" />
      </IconButton>

      <Menu
        id="datadog-message-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {items.map(({ label, href }) => (
          // Real anchors, so middle-click and "copy link address" work.
          <MenuItem
            key={label}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setAnchorEl(null)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
